import { type JSX, type ReactNode } from 'react';

import { type Destination } from '../routes';
import { mediaSrc, useContent, useContentOverride } from '../content/ContentProvider';

/**
 * The small shared pieces. Deliberately few — an abstraction with one caller is
 * just indirection.
 */

interface ContainerProps {
  readonly children: ReactNode;
  readonly className?: string;
  /** Defaults to `div`; the header and footer pass their own landmark. */
  readonly as?: 'div' | 'header' | 'footer' | 'nav' | 'section';
}

/**
 * The one horizontal content container.
 *
 * Every left-aligned block on the page lines up on the same two edges. Before
 * this existed the page had two competing containers: `--page` centred, used
 * by Solutions, Partner portal, Grow and the download panel, and a plain
 * `--gutter` inset used by the header, News and the footer. At 1440px that put
 * the logo 81px left of the headings underneath it.
 *
 * The measured winner is the centred one — the two left-aligned section
 * headings on the page, Solutions and Grow, both sit on it — so this is that
 * rule extracted, not a new width invented. `--page` and `--gutter` are the
 * existing tokens and are unchanged; on narrow viewports the gutter is
 * naturally what binds, so the two rules converge and nothing moves below
 * about 1280px.
 *
 * Full-bleed elements — the carousels, which deliberately run to the viewport
 * edge — stay outside this container. Wrapping them would undo that.
 */
export function PageContainer({ children, className, as = 'div' }: ContainerProps): JSX.Element {
  const Tag = as;
  return <Tag className={['jt-container', className ?? ''].filter(Boolean).join(' ')}>{children}</Tag>;
}

interface DestinationLinkProps {
  readonly destination: Destination;
  readonly children: ReactNode;
  readonly className?: string;
  /** Canonical analytics key. Never carries personal data. */
  readonly analyticsKey?: string;
}

/**
 * Links to a platform destination, honestly.
 *
 * Most of the platform is not built. Rather than emit `href="#"` — which looks
 * like a link, does nothing, and hides how much is missing — an unavailable
 * destination renders as a disabled `button` whose accessible name says so.
 *
 * That distinction matters most for keyboard users: a control that takes focus
 * and explains itself beats a link that takes focus and then does nothing at
 * all.
 *
 * ## `aria-disabled`, not `disabled`
 *
 * This used to set the `disabled` attribute, which removes the control from
 * the tab order entirely — the exact opposite of the sentence above. On a page
 * with a handful of them that was merely wrong; in the mega menu, where nearly
 * all fifty-four destinations are unbuilt, it left a keyboard user with one
 * reachable control in the whole panel and no way to read the menu at all.
 *
 * `aria-disabled` announces the state without removing the tab stop, which is
 * what the WAI-ARIA practices recommend for a control that must stay
 * discoverable. Activation is suppressed explicitly instead, since an
 * `aria-disabled` button is still a live button as far as the browser cares.
 */
export function DestinationLink({
  destination,
  children,
  className,
  analyticsKey,
}: DestinationLinkProps): JSX.Element {
  // A label edited in the dashboard replaces the one declared on the route, so
  // one edit changes the mega menu, the footer and every other rendering of
  // that destination at once. `children` still wins where a caller passed its
  // own text — the hero's buttons, for instance, have their own content block.
  const label = useNavLabel(destination);
  if (destination.availability === 'coming-soon') {
    return (
      <button
        type="button"
        className={['jt-link', 'jt-link--soon', className ?? ''].filter(Boolean).join(' ')}
        aria-disabled="true"
        // `aria-disabled` leaves the button live, so activation is refused
        // here rather than by the browser.
        onClick={(event) => event.preventDefault()}
        title={`${destination.label} — coming soon`}
        data-analytics-key={analyticsKey}
      >
        {label ?? children}
        <span className="jt-visually-hidden"> (coming soon)</span>
      </button>
    );
  }

  return (
    <a
      href={destination.path}
      className={['jt-link', className ?? ''].filter(Boolean).join(' ')}
      data-analytics-key={analyticsKey}
    >
      {label ?? children}
    </a>
  );
}

/**
 * The edited label for a destination, or `undefined` to keep what was passed.
 *
 * Only replaces `children` when the caller rendered the destination's own name.
 * A button reading "Get Just Treated" points at Sign up but is not the Sign up
 * label, and overwriting it would rewrite the hero from the navigation editor.
 */
function useNavLabel(destination: Destination): string | undefined {
  return useContentOverride()(`nav.${destination.key}` as never);
}

interface ButtonProps {
  readonly destination: Destination;
  readonly children: ReactNode;
  /** Matches the mockup's button palette. */
  readonly variant?: 'ink' | 'violet' | 'lavender' | 'white' | 'onCard';
  readonly shape?: 'pill' | 'rounded';
  readonly analyticsKey?: string;
}

export function JTButton({
  destination,
  children,
  variant = 'ink',
  shape = 'pill',
  analyticsKey,
}: ButtonProps): JSX.Element {
  const className = `jt-button jt-button--${variant} jt-button--${shape}`;

  if (destination.availability === 'coming-soon') {
    return (
      <button
        type="button"
        className={`${className} jt-button--soon`}
        aria-disabled="true"
        onClick={(event) => event.preventDefault()}
        title={`${destination.label} — coming soon`}
        data-analytics-key={analyticsKey}
      >
        {children}
        <span className="jt-visually-hidden"> (coming soon)</span>
      </button>
    );
  }

  return (
    <a href={destination.path} className={className} data-analytics-key={analyticsKey}>
      {children}
    </a>
  );
}

interface MediaProps {
  readonly alt: string;
  /** Selects the slot's tint and caption treatment. */
  readonly variant: string;
  readonly caption?: string;
  readonly className?: string;
  /**
   * The image block this slot renders, if it has one.
   *
   * When the block holds an uploaded asset the photograph is rendered; when it
   * is empty the hatched placeholder below is, exactly as before. That is what
   * makes every slot replaceable from the dashboard without changing how an
   * un-replaced page looks.
   */
  readonly contentKey?: string;
}

/**
 * A composed image slot.
 *
 * The mockup marks each photograph as a hatched placeholder with a caption
 * describing the shot to commission. That is reproduced here rather than
 * invented around: no stock photography is substituted, and nothing is copied
 * from the reference, which would be infringement.
 *
 * The caption is `aria-hidden` and the slot carries the real alt text it will
 * keep once the photograph exists, so swapping in an `<img>` later is a change
 * inside this component alone.
 */
export function JTMedia({ alt, variant, caption, className, contentKey }: MediaProps): JSX.Element {
  const content = useContent();
  const source = contentKey === undefined ? undefined : mediaSrc(content(contentKey as never));

  if (source !== undefined) {
    return (
      <img
        className={['jt-media', `jt-media--${variant}`, 'jt-media--filled', className ?? '']
          .filter(Boolean)
          .join(' ')}
        src={source}
        // The slot's own alt text, not the uploader's. The upload's alt
        // describes the file; this describes what the picture is doing here,
        // and the two are not the same sentence.
        alt={alt}
        loading="lazy"
        decoding="async"
      />
    );
  }

  return (
    <div
      className={['jt-media', `jt-media--${variant}`, className ?? ''].filter(Boolean).join(' ')}
      role="img"
      aria-label={alt}
    >
      {caption !== undefined ? (
        <span className="jt-media__caption" aria-hidden="true">
          {caption}
        </span>
      ) : null}
    </div>
  );
}

/**
 * An editable button: its label and its link both come from the dashboard.
 *
 * The same honesty as `DestinationLink`: a blank link is a destination that does
 * not exist yet, so it renders as an announced, focusable, inert control rather
 * than `href="#"`. A link can be edited to point somewhere real at any time
 * without a deployment.
 */
export function CmsAction({
  href,
  children,
  className,
  analyticsKey,
}: {
  readonly href: string | undefined;
  readonly children: ReactNode;
  readonly className: string;
  readonly analyticsKey?: string;
}): JSX.Element {
  if (href === undefined) {
    return (
      <button
        type="button"
        className={`${className} jt-button--soon`}
        aria-disabled="true"
        onClick={(event) => event.preventDefault()}
        data-analytics-key={analyticsKey}
      >
        {children}
        <span className="jt-visually-hidden"> (coming soon)</span>
      </button>
    );
  }

  return (
    <a href={href} className={className} data-analytics-key={analyticsKey}>
      {children}
    </a>
  );
}

/**
 * A carousel arrow.
 *
 * `atEnd` is the design's end state: the arrow that cannot move any further
 * takes its resting shade. It stays focusable and announces itself as
 * unavailable rather than vanishing from the tab order, and a press at the end
 * does nothing.
 */
export function JTArrow({
  direction,
  label,
  onClick,
  disabled,
  atEnd = false,
  tone = 'grey',
  size = 20,
}: {
  readonly direction: 'prev' | 'next';
  readonly label: string;
  readonly onClick: () => void;
  readonly disabled?: boolean;
  readonly atEnd?: boolean;
  /**
   * `grey` — Explore's 48px lavender circles.
   * `solid` / `quiet` — Solutions' pair: the arrow that leads is filled violet.
   * `ring` — the Partner Portal's white circles with a violet ring.
   */
  readonly tone?: 'grey' | 'violet' | 'outline' | 'solid' | 'quiet' | 'ring';
  readonly size?: number;
}): JSX.Element {
  return (
    <button
      type="button"
      className={`jt-arrow jt-arrow--${tone} ${atEnd ? 'jt-arrow--end' : ''}`}
      onClick={() => {
        if (!atEnd) onClick();
      }}
      disabled={disabled}
      aria-disabled={atEnd ? 'true' : undefined}
      aria-label={label}
    >
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d={direction === 'prev' ? 'M15 6 L9 12 L15 18' : 'M9 6 L15 12 L9 18'}
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}
